import { defineStore } from 'pinia';
import { computed, Ref } from 'vue';
import { useAuthStore } from '@/stores/index';
import { IPaginationDTO, ITeamDTO, IUserDTO, PaginationDTO, TeamDTO, UserDTO } from '@/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { getMyTeamAPI, getTeamLeadIdAPI, updateMyTeamAPI, userCreateNewTeamAPI } from '@/services';
import {
  createTeamAPI,
  deleteTeamAPI,
  getAllTeamsAPI,
  getTeamById,
  getTeamsForYearAPI,
  getTeamsForYearWithCheckinAPI,
  getTeamYearAPI,
  saveTeamAPI,
  teamCheckInAPI,
  teamCheckOutAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from './updateStatus';
export const useTeamStore = defineStore(
  'team',
  () => {
    const authStore = useAuthStore();
    const queryClient = useQueryClient();
    const statusStore = useUpdateStatusStore();

    const getMyTeamQuery = useQuery({
      queryKey: computed(() => ['my-team']),
      queryFn: async (): Promise<ITeamDTO> => {
        const ret = await getMyTeamAPI();
        return new TeamDTO(ret);
      },
      enabled: computed(() => {
        return !!authStore.isLoggedIn && authStore.role === 'Team';
      }),
    });
    const getTeamForBranchYear = (teamId: Ref<string>, yearId: Ref<string>) => {
      return useQuery({
        queryKey: computed(() => ['team', teamId.value, yearId.value]),
        queryFn: async (): Promise<ITeamDTO> => {
          const ret = await getTeamYearAPI(yearId.value, teamId.value);
          const team = new TeamDTO(ret);
          return team;
        },
        enabled: computed(() => !!yearId.value && !!teamId.value),
      });
    };

    const getTeamQuery = (teamId: Ref<string>) => {
      return useQuery({
        queryKey: computed(() => ['team', teamId.value]),
        queryFn: async (): Promise<ITeamDTO> => {
          const ret = await getTeamById(teamId.value);
          const team = new TeamDTO(ret);
          return team;
        },
        enabled: computed(() => !!teamId.value),
      });
    };

    const getTeamLeadQuery = (teamId: Ref<string>, yearId: Ref<string>) => {
      return useQuery({
        queryKey: computed(() => ['team-lead', teamId.value, yearId.value]),
        queryFn: async (): Promise<IUserDTO> => {
          const ret = await getTeamLeadIdAPI(teamId.value, yearId.value);
          const team = new UserDTO(ret);
          return team;
        },
        enabled: computed(() => !!teamId.value && !!yearId.value),
      });
    };

    const getAllTeamsQuery = (pagination: Ref<IPaginationDTO>) => {
      return useQuery({
        queryKey: computed(() => ['teams', pagination]),
        queryFn: async (): Promise<{ data: ITeamDTO[]; count: number }> => {
          const ret = await getAllTeamsAPI(pagination.value);
          const teams = await Promise.all(ret.data.map((team) => new TeamDTO(team)));
          return { data: teams, count: ret.count };
        },
        enabled: computed(() => !!pagination.value),
      });
    };

    const getTeamsForYearWithCheckin = (
      branchId: Ref<string>,
      yearId: Ref<string>,
      pagination?: Ref<IPaginationDTO>,
    ) => {
      return useQuery({
        queryKey: computed(() => [
          'teams-checkin',
          branchId.value,
          yearId.value,
          pagination?.value,
        ]),
        queryFn: async (): Promise<{ data: ITeamDTO[]; count: number }> => {
          const ret = await getTeamsForYearWithCheckinAPI(
            branchId.value,
            yearId.value,
            pagination?.value ?? new PaginationDTO(),
          );
          const teams = ret.data.map((team) => new TeamDTO(team));

          return { data: teams, count: ret.count };
        },
        enabled: computed(() => !!yearId.value && !!branchId.value),
      });
    };

    const getTeamsForYearQuery = (year: Ref<string>, pagination?: Ref<IPaginationDTO>) => {
      return useQuery({
        queryKey: computed(() => ['teams-year', year.value, pagination?.value]),
        queryFn: async (): Promise<{ data: ITeamDTO[]; count: number }> => {
          const ret = await getTeamsForYearAPI(
            year.value,
            pagination?.value ?? new PaginationDTO(),
          );
          const teams = ret.data.map((team) => new TeamDTO(team));

          return { data: teams, count: ret.count };
        },
        enabled: computed(() => !!year.value),
      });
    };

    const myTeam = computed(() => {
      return getMyTeamQuery.data.value;
    });

    const belongs = computed(() => {
      if (getMyTeamQuery.isPending.value || !myTeam.value) {
        return false;
      }

      return !!myTeam.value.name && myTeam.value.name.trim() !== '';
    });

    const isLeader = computed(() => {
      return false;
    });

    const forceTeamUpdate = async () => {
      if (authStore.role === 'Team') {
        queryClient.invalidateQueries({ queryKey: ['my-team'] });
      }
    };

    // Mutations
    const create = useMutation({
      mutationFn: (team: ITeamDTO) => {
        if (authStore.isAdmin) {
          return createTeamAPI(team);
        } else {
          return userCreateNewTeamAPI(team);
        }
      },
      onSuccess: () => {
        if (authStore.role === 'Team') {
          queryClient.invalidateQueries({ queryKey: ['my-team'] });
        }
        statusStore.setSuccessStatus('team-create', 'success:create');
      },
      onError: (err: string) => {
        statusStore.setErrorStatus(`team-create`, err);
      },
    });

    const update = useMutation({
      mutationFn: (team: ITeamDTO) => {
        if (authStore.isAdmin) {
          return saveTeamAPI(team);
        } else {
          return updateMyTeamAPI(team);
        }
      },
      onSuccess: (_data, team: ITeamDTO) => {
        if (authStore.role === 'Team') {
          queryClient.invalidateQueries({ queryKey: ['my-team'] });
        } else {
          queryClient.invalidateQueries({ queryKey: [`team-${team.id}`] });
        }
        queryClient.invalidateQueries({ queryKey: ['team', team.id] });
        statusStore.setSuccessStatus(`team-${team.id}`, 'success:update');
      },
      onError: (err: string, team: ITeamDTO) => {
        statusStore.setErrorStatus(`team-${team.id}`, err);
      },
    });

    const checkIn = useMutation({
      mutationFn: ({ teamId, yearId }: { teamId: string; yearId: string }) => {
        return teamCheckInAPI(teamId, yearId);
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['teams-checkin'] });
        queryClient.invalidateQueries({ queryKey: [`team-${variables.teamId}`] });
        statusStore.setSuccessStatus(`team-checkin-${variables.teamId}`, 'success:update');
      },
      onError: (err: string, variables) => {
        statusStore.setErrorStatus(`team-checkin-${variables.teamId}`, err);
      },
    });

    const checkOut = useMutation({
      mutationFn: ({ teamId, yearId }: { teamId: string; yearId: string }) => {
        return teamCheckOutAPI(teamId, yearId);
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['teams-checkin'] });
        queryClient.invalidateQueries({ queryKey: [`team-${variables.teamId}`] });
        statusStore.setSuccessStatus(`team-checkin-${variables.teamId}`, 'success:update');
      },
      onError: (err: string, variables) => {
        statusStore.setErrorStatus(`team-checkin-${variables.teamId}`, err);
      },
    });

    const remove = useMutation({
      mutationFn: (teamId: string) => {
        if (authStore.isAdmin) {
          return deleteTeamAPI(teamId);
        } else {
          throw 'Not allowed to delete team';
        }
      },
      onSuccess: (_data, teamId: string) => {
        queryClient.invalidateQueries({ queryKey: [`team-${teamId}`] });
        statusStore.setSuccessStatus(`team-${teamId}`, 'success:delete');
      },
      onError: (err: string, teamId: string) => {
        statusStore.setErrorStatus(`team-${teamId}`, err);
      },
    });

    return {
      myTeam,
      getTeamQuery,
      getTeamForBranchYear,
      getAllTeamsQuery,
      getTeamLeadQuery,
      belongs,
      getMyTeamQuery,
      isLeader,
      forceTeamUpdate,
      create,
      update,
      remove,
      getTeamsForYearWithCheckin,
      checkIn,
      checkOut,
      getTeamsForYearQuery,
    };
  },
  {
    persist: true,
  },
);
